import { useEffect, useSyncExternalStore } from 'react';
import type { CompanyDetailsQuery } from '@/types/enterprise';
import { api } from '@/lib/api/client';
import { authClient } from '@/lib/firebase';

export type Me = CompanyDetailsQuery['me'] | null;

type State = { data: Me; fetching: boolean; error?: Error; loaded: boolean; authUid: string | null };

let state: State = { data: null, fetching: false, loaded: false, authUid: null };
const listeners = new Set<() => void>();
let inflight: Promise<Me> | null = null;

function notify() {
  listeners.forEach((listener) => listener());
}

function setState(next: Partial<State>) {
  state = { ...state, ...next };
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

export async function fetchMe(force = false): Promise<Me> {
  const authUid = authClient().currentUser?.uid ?? null;
  if (state.loaded && state.authUid !== authUid) {
    resetMe();
  }
  if (!force && state.loaded) return state.data;
  if (!force && inflight) return inflight;
  setState({ fetching: true, error: undefined });
  inflight = (async () => {
    try {
      const me = (await api.get('/api/v1/enterprise/users/me')) as Me;
      if ((authClient().currentUser?.uid ?? null) !== authUid) {
        resetMe();
        return state.data;
      }
      setState({ data: me, loaded: true, authUid });
      return me;
    } catch (e) {
      setState({ error: e instanceof Error ? e : new Error(String(e)) });
      throw e;
    } finally {
      inflight = null;
      setState({ fetching: false });
    }
  })();
  return inflight;
}

export function resetMe() {
  state = { data: null, fetching: false, error: undefined, loaded: false, authUid: null };
  inflight = null;
  notify();
}

export function useMe() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    if (!state.loaded && !state.fetching) {
      fetchMe().catch(() => {});
    }
  }, []);
  return {
    data: snapshot.data,
    fetching: snapshot.fetching,
    error: snapshot.error,
    refetch: () => fetchMe(true),
  };
}
